import { z } from 'zod';
/**
 * THE MAP STORE'S POINTER FILE, AND THE NAMES IN THE STORE (ADR-023 section 4, #746).
 *
 * Each site's map store holds the Virginia map file under a new name per refresh, the label fonts, and
 * one small file, `current.json`, naming the map file in use and its SHA-256. The refresh workflow
 * writes the pointer (scripts/map-refresh.mjs), the server reads it
 * (apps/api/src/integrations/storage/map-store.ts) and reports it on `/api/health`. One schema, here,
 * for all three (CLAUDE.md rule 3).
 */
/**
 * The one shape a map file's name may have: `virginia-<Protomaps build, YYYYMMDD>-<cut run id>-<bytes>.pmtiles`.
 *
 * Anchored, digits only between fixed words, so a name can never hold a `/`, a `..` or anything but
 * these. The run id makes every cut's name new. The size is in the name so that the cut, which signs
 * in to nothing, can compare its file with the one the test site serves from the name `/api/health`
 * reports alone.
 */
export const MAP_FILE_NAME_PATTERN = /^virginia-(20[0-9]{6})-([1-9][0-9]{0,19})-([1-9][0-9]{0,11})\.pmtiles$/;
export const mapFileNameSchema = z.string().regex(MAP_FILE_NAME_PATTERN);
/** Lower-case hex SHA-256, exactly 64 characters. */
export const sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/);
/**
 * `current.json`: the map file in use and its fingerprint, and nothing else. `strict`, so a pointer
 * that carries any other key is refused rather than half-read.
 */
export const mapPointerSchema = z.object({ file: mapFileNameSchema, sha256: sha256HexSchema }).strict();
/** The pointer's own name in the container. */
export const MAP_POINTER_BLOB = 'current.json';
/** What a map file's name says, or null for a name that does not have the one shape. */
export function parseMapFileName(name) {
    const match = MAP_FILE_NAME_PATTERN.exec(name);
    if (match === null)
        return null;
    return { build: match[1], cutRunId: match[2], bytes: Number(match[3]) };
}
/**
 * The label fonts the map style uses, a closed list (ADR-023 section 3): Noto Sans, from Protomaps'
 * basemaps-assets, SIL Open Font License. Each is a set of glyph files of 256 code points each.
 */
export const MAP_FONT_STACKS = ['Noto Sans Regular', 'Noto Sans Medium', 'Noto Sans Italic'];
/** `0-255`, `256-511`, … `65280-65535`: the 256 glyph ranges of each font. */
export const MAP_FONT_RANGES = Object.freeze(Array.from({ length: 256 }, (_, i) => `${i * 256}-${i * 256 + 255}`));
/** Where one font range lives in the container. */
export function mapFontBlobName(stack, range) {
    return `fonts/${stack}/${range}.pbf`;
}
/** The fonts' licence, kept beside them. */
export const MAP_FONT_LICENCE_BLOB = 'fonts/OFL.txt';
/** Every font file the store holds, licence included: 3 × 256 + 1. */
export const MAP_FONT_BLOBS = Object.freeze([
    ...MAP_FONT_STACKS.flatMap((stack) => MAP_FONT_RANGES.map((range) => mapFontBlobName(stack, range))),
    MAP_FONT_LICENCE_BLOB,
]);
/**
 * THE TWO MAP ROUTES (ADR-023 sections 5 and 7, #747), as the browser asks them and the server checks them.
 *
 * The map file holds detail down to zoom 14 (ADR-023 section 4); MapLibre draws zoom 15 from zoom-14 squares, so it
 * never asks for more. The server refuses any other zoom, and any column or row outside that zoom, before it reads
 * storage. The browser's style names exactly these two addresses and no other (`tests/map-style.test.ts`).
 */
export const MAP_SQUARE_MAX_ZOOM = 14;
/** The square route, with MapLibre's placeholders. Our own address only: the browser joins it to the page's origin. */
export const MAP_SQUARES_PATH = '/api/map/squares/{z}/{x}/{y}';
/** The font route, with MapLibre's placeholders. `{range}` is one of `MAP_FONT_RANGES`. */
export const MAP_FONTS_PATH = '/api/map/fonts/{fontstack}/{range}.pbf';
/** A whole number in its one spelling: no sign, no leading zero, at most five digits. */
const CANONICAL_NUMBER = /^(0|[1-9][0-9]{0,4})$/;
/**
 * A square's zoom, column and row as the route received them, checked: canonical whole numbers, the zoom from 0 to
 * `MAP_SQUARE_MAX_ZOOM`, column and row inside that zoom. Null for anything else. Nothing is read for a null.
 */
export function parseMapSquare(z, x, y) {
    if (z === undefined || x === undefined || y === undefined)
        return null;
    if (!CANONICAL_NUMBER.test(z) || !CANONICAL_NUMBER.test(x) || !CANONICAL_NUMBER.test(y))
        return null;
    const [zoom, column, row] = [Number(z), Number(x), Number(y)];
    if (zoom > MAP_SQUARE_MAX_ZOOM)
        return null;
    const across = 2 ** zoom;
    return column < across && row < across ? { z: zoom, x: column, y: row } : null;
}
/**
 * A font range as the route received it, checked against the closed lists: one of `MAP_FONT_STACKS` exactly (never a
 * comma-joined list of stacks), and one of `MAP_FONT_RANGES` followed by `.pbf`. Null for anything else.
 */
export function parseMapFont(stack, file) {
    const known = MAP_FONT_STACKS.find((name) => name === stack);
    if (known === undefined || file === undefined || !file.endsWith('.pbf'))
        return null;
    const range = file.slice(0, -'.pbf'.length);
    return MAP_FONT_RANGES.includes(range) ? { stack: known, range } : null;
}
//# sourceMappingURL=map.js.map